import { useEffect, useState } from "react";
import { Gift, Loader2, Euro, Package, Trash2, PartyPopper, Target, ThumbsUp, Plus, Lightbulb, Check, Star, Link2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BrutalButton } from "@/components/Brutal";
import api from "@/lib/api";
import { toast } from "sonner";

export const GiftPotDialog = ({ open, onClose, birthdayUserId, potId, onDeleted }) => {
  const gid = potId || birthdayUserId;
  const [pot, setPot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("money");
  const [amount, setAmount] = useState("");
  const [item, setItem] = useState("");
  const [saving, setSaving] = useState(false);
  const [goalInput, setGoalInput] = useState("");
  const [newIdea, setNewIdea] = useState("");
  const [linkInput, setLinkInput] = useState("");

  const load = async () => {
    if (!gid) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/giftpots/${gid}`);
      setPot(data);
      setGoalInput(data.goal ? String(data.goal) : "");
      setLinkInput(data.payment_link || "");
      if (data.mine) {
        setType(data.mine.type);
        setAmount(data.mine.type === "money" ? String(data.mine.amount || "") : "");
        setItem(data.mine.type === "item" ? data.mine.item || "" : "");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Kon cadeaupot niet laden"); onClose(); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, gid]);

  const saveLink = async () => {
    try { await api.put(`/giftpots/${gid}/payment-link`, { payment_link: linkInput.trim() }); toast.success("Betaal-link opgeslagen 💸"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); }
  };

  const removePot = async () => {
    if (!window.confirm("Deze cadeaupot verwijderen?")) return;
    try { await api.delete(`/giftpots/${gid}`); toast.success("Cadeaupot verwijderd"); onClose(); onDeleted && onDeleted(); }
    catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); }
  };

  const submit = async () => {
    if (type === "money" && !(Number(amount) > 0)) return toast.error("Vul een bedrag in");
    if (type === "item" && !item.trim()) return toast.error("Beschrijf wat je meeneemt");
    setSaving(true);
    try {
      await api.post(`/giftpots/${gid}/contribute`, { type, amount: Number(amount) || 0, item: item.trim() });
      toast.success("Bijdrage opgeslagen 🎁");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); }
    finally { setSaving(false); }
  };

  const removeMine = async () => {
    try { await api.delete(`/giftpots/${gid}/contribute`); setAmount(""); setItem(""); toast.success("Bijdrage verwijderd"); await load(); }
    catch { toast.error("Mislukt"); }
  };

  const saveGoal = async () => {
    try { await api.put(`/giftpots/${gid}/goal`, { goal: Number(goalInput) || 0 }); toast.success("Streefbedrag opgeslagen 🎯"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); }
  };

  const addIdea = async () => {
    if (!newIdea.trim()) return;
    try { await api.post(`/giftpots/${gid}/ideas`, { text: newIdea.trim() }); setNewIdea(""); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); }
  };

  const voteIdea = async (id) => {
    try { await api.post(`/giftpots/${gid}/ideas/${id}/vote`); await load(); }
    catch { toast.error("Mislukt"); }
  };

  const removeIdea = async (id) => {
    try { await api.delete(`/giftpots/${gid}/ideas/${id}`); await load(); }
    catch { toast.error("Mislukt"); }
  };

  const chooseIdea = async (id) => {
    try { await api.post(`/giftpots/${gid}/ideas/${id}/choose`); await load(); }
    catch { toast.error("Mislukt"); }
  };

  const toggleClosed = async () => {
    try { const { data } = await api.post(`/giftpots/${gid}/close`); toast.success(data.closed ? "Cadeaupot afgerond 🎉" : "Cadeaupot heropend"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); }
  };

  const goal = Number(pot?.goal || 0);
  const total = Number(pot?.total_money || 0);
  const pct = goal > 0 ? Math.min(100, Math.round((total / goal) * 100)) : 0;

  const bu = pot?.birthday_user;
  const isCustom = pot?.kind === "custom";
  const cu = pot?.custom;
  const fmtDate = (d) => new Date(d).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[90vh] overflow-auto no-scrollbar" data-testid="giftpot-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-head font-black text-xl flex items-center gap-2">
            <Gift className="w-5 h-5" strokeWidth={2.5} />Cadeaupot
          </DialogTitle>
        </DialogHeader>
        {loading || !pot ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            <div className="brutal-sm rounded-xl p-4 bg-brand-pink/15 flex items-center gap-3">
              <PartyPopper className="w-6 h-6 shrink-0" strokeWidth={2.5} />
              {isCustom ? (
                <div className="min-w-0">
                  <p className="font-head font-black truncate">{cu?.title}</p>
                  <p className="text-xs font-semibold text-brand-ink/60">
                    {cu?.recipient_name ? `Voor ${cu.recipient_name}` : "Voor het huis"}{cu?.date ? ` · ${fmtDate(cu.date)}` : ""}
                  </p>
                  {cu?.recipient_name && <p className="text-xs font-bold text-brand-ink/50 mt-0.5">Psst — houd het geheim! 🤫</p>}
                </div>
              ) : (
                <div>
                  <p className="font-head font-black">{bu?.name} wordt {bu?.age} 🎂</p>
                  {bu?.date && <p className="text-xs font-semibold text-brand-ink/60">{fmtDate(bu.date)}</p>}
                  <p className="text-xs font-bold text-brand-ink/50 mt-0.5">Psst — houd het geheim! 🤫</p>
                </div>
              )}
            </div>

            {pot.closed && (
              <div data-testid="giftpot-closed-banner" className="brutal-sm rounded-xl p-3 bg-brand-green text-white flex items-center gap-2 font-bold text-sm">
                <Check className="w-5 h-5 shrink-0" strokeWidth={3} />Cadeau geregeld — deze pot is afgerond! 🎁
              </div>
            )}

            <div className="brutal-sm rounded-xl p-4 bg-brand-green/15 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-bold text-sm">Samen gespaard</span>
                <span data-testid="giftpot-total" className="font-head font-black text-lg">€ {total.toFixed(2)}{goal > 0 && <span className="text-brand-ink/50 text-sm"> / € {goal.toFixed(2)}</span>}</span>
              </div>
              {goal > 0 && (
                <div className="h-3 rounded-full border-2 border-brand-ink bg-white overflow-hidden">
                  <div data-testid="giftpot-progress" className="h-full bg-brand-green transition-all" style={{ width: `${pct}%` }} />
                </div>
              )}
              {goal > 0 && <p className="text-xs font-bold text-brand-ink/60">{pct}% behaald{total < goal ? ` — nog € ${(goal - total).toFixed(2)} te gaan` : " — doel gehaald! 🎉"}</p>}
              {!pot.closed && (
              <div className="flex items-center gap-2 pt-1">
                <Target className="w-4 h-4 shrink-0" strokeWidth={2.5} />
                <span className="text-xs font-semibold text-brand-ink/60">Streefbedrag €</span>
                <input data-testid="giftpot-goal-input" type="number" min="0" step="1" value={goalInput} onChange={(e) => setGoalInput(e.target.value)} placeholder="0" className="w-20 brutal-sm rounded-lg px-2 py-1.5 font-medium outline-none bg-white" />
                <button data-testid="giftpot-goal-save" onClick={saveGoal} className="brutal-sm brutal-press rounded-lg px-3 py-1.5 text-xs font-bold bg-white">Zet</button>
              </div>
              )}
            </div>

            <div className="brutal-sm rounded-xl p-4 bg-brand-blue/10 space-y-2" data-testid="giftpot-payment">
              <p className="font-head font-bold text-sm flex items-center gap-1.5"><Link2 className="w-4 h-4" strokeWidth={2.5} />Geld ophalen</p>
              {pot.payment_link ? (
                <a data-testid="giftpot-pay-link" href={pot.payment_link.startsWith("http") ? pot.payment_link : undefined}
                  target="_blank" rel="noreferrer"
                  className="block brutal-sm brutal-press rounded-xl px-3 py-2.5 bg-brand-green text-center font-bold text-sm break-all">
                  💸 Betaal / draag bij {pot.payment_link.startsWith("http") ? "(open link)" : `— ${pot.payment_link}`}
                </a>
              ) : (
                <p className="text-xs text-brand-ink/60 font-semibold">Nog geen betaal-link. Voeg een Tikkie-link of IBAN toe zodat iedereen makkelijk kan overmaken.</p>
              )}
              {!pot.closed && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <input data-testid="giftpot-link-input" value={linkInput} onChange={(e) => setLinkInput(e.target.value)} placeholder="https://tikkie.me/… of IBAN NL.." className="flex-1 min-w-0 brutal-sm rounded-lg px-2 py-1.5 text-sm font-medium outline-none bg-white" />
                    <button data-testid="giftpot-link-save" onClick={saveLink} className="brutal-sm brutal-press rounded-lg px-3 py-1.5 text-xs font-bold bg-white shrink-0">Zet</button>
                  </div>
                  <p className="text-[11px] font-semibold text-brand-ink/60 leading-snug">💡 Tip: kies in Tikkie de optie <b>"vrij te kiezen bedrag"</b>, zodat iedereen zelf bepaalt hoeveel hij bijdraagt.</p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <p className="font-head font-bold text-sm text-brand-ink/60 flex items-center gap-1.5"><Lightbulb className="w-4 h-4" strokeWidth={2.5} />Cadeau-ideeën</p>
              {(pot.ideas || []).length === 0 && <p className="text-sm text-brand-ink/50">Nog geen ideeën — opper er een! 💡</p>}
              {(pot.ideas || []).map((idea) => (
                <div key={idea.id} data-testid={`giftpot-idea-${idea.id}`} className={`brutal-sm rounded-xl px-3 py-2 flex items-center gap-2 text-sm ${idea.chosen ? "bg-brand-yellow" : "bg-white"}`}>
                  <button data-testid={`giftpot-vote-${idea.id}`} onClick={() => voteIdea(idea.id)}
                    className={`brutal-sm brutal-press rounded-full px-2.5 py-1 flex items-center gap-1 font-bold text-xs shrink-0 ${idea.voted ? "bg-brand-violet text-white" : "bg-brand-cream"}`}>
                    <ThumbsUp className="w-3.5 h-3.5" strokeWidth={2.5} />{idea.votes}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate flex items-center gap-1">{idea.text}{idea.chosen && <span className="text-[10px] font-black bg-brand-ink text-brand-yellow rounded-full px-1.5 py-0.5 shrink-0">🎁 GEKOZEN</span>}</p>
                    <p className="text-[11px] text-brand-ink/50 font-semibold">door {idea.name}</p>
                  </div>
                  <button data-testid={`giftpot-choose-${idea.id}`} onClick={() => chooseIdea(idea.id)} title={idea.chosen ? "Keuze ongedaan maken" : "Kies dit cadeau"}
                    className={`shrink-0 brutal-sm brutal-press rounded-full p-1.5 ${idea.chosen ? "bg-brand-ink text-brand-yellow" : "bg-brand-cream text-brand-ink/50"}`}>
                    {idea.chosen ? <Check className="w-4 h-4" strokeWidth={3} /> : <Star className="w-4 h-4" strokeWidth={2.5} />}
                  </button>
                  {idea.mine && <button data-testid={`giftpot-idea-del-${idea.id}`} onClick={() => removeIdea(idea.id)} className="shrink-0 text-brand-ink/40 hover:text-red-500"><Trash2 className="w-4 h-4" strokeWidth={2.5} /></button>}
                </div>
              ))}
              {!pot.closed && (
              <div className="flex gap-2">
                <input data-testid="giftpot-idea-input" value={newIdea} onChange={(e) => setNewIdea(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addIdea()} placeholder="Opper een cadeau-idee…" className="flex-1 brutal-sm rounded-xl px-3 py-2 font-medium outline-none" />
                <button data-testid="giftpot-idea-add" onClick={addIdea} className="brutal-sm brutal-press rounded-xl px-3 bg-brand-yellow"><Plus className="w-4 h-4" strokeWidth={2.5} /></button>
              </div>
              )}
            </div>

            <div className="space-y-2">
              <p className="font-head font-bold text-sm text-brand-ink/60">Wie doet wat?</p>
              {pot.contributions.length === 0 && <p className="text-sm text-brand-ink/50">Nog geen bijdragen — wees de eerste! 🎉</p>}
              {pot.contributions.map((c, i) => (
                <div key={i} data-testid={`giftpot-contrib-${i}`} className="brutal-sm rounded-xl px-3 py-2 bg-white flex items-center gap-2 text-sm font-bold">
                  {c.type === "money" ? <Euro className="w-4 h-4 shrink-0" strokeWidth={2.5} /> : <Package className="w-4 h-4 shrink-0" strokeWidth={2.5} />}
                  <span className="flex-1 min-w-0 truncate">{c.name}</span>
                  <span className="text-brand-ink/70">{c.type === "money" ? `€ ${Number(c.amount).toFixed(2)}` : c.item}</span>
                </div>
              ))}
            </div>

            {!pot.closed && !pot.i_opted_out && (
            <div className="brutal-sm rounded-xl p-4 bg-brand-cream space-y-3">
              <p className="font-head font-bold text-sm">Mijn bijdrage</p>
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="giftpot-type-money" onClick={() => setType("money")} className={`brutal-sm brutal-press rounded-xl px-3 py-2.5 flex items-center justify-center gap-2 font-bold text-sm ${type === "money" ? "bg-brand-violet text-white" : "bg-white"}`}>
                  <Euro className="w-4 h-4" strokeWidth={2.5} />Geld
                </button>
                <button data-testid="giftpot-type-item" onClick={() => setType("item")} className={`brutal-sm brutal-press rounded-xl px-3 py-2.5 flex items-center justify-center gap-2 font-bold text-sm ${type === "item" ? "bg-brand-violet text-white" : "bg-white"}`}>
                  <Package className="w-4 h-4" strokeWidth={2.5} />Ik neem mee
                </button>
              </div>
              {type === "money" ? (
                <div className="flex items-center gap-2">
                  <span className="font-black text-lg">€</span>
                  <input data-testid="giftpot-amount" type="number" min="0" step="0.50" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="10,00" className="flex-1 brutal-sm rounded-xl px-3 py-2.5 font-medium outline-none" />
                </div>
              ) : (
                <input data-testid="giftpot-item" value={item} onChange={(e) => setItem(e.target.value)} placeholder="bv. taart, ballonnen, een boek…" className="w-full brutal-sm rounded-xl px-3 py-2.5 font-medium outline-none" />
              )}
              <div className="flex gap-2">
                <BrutalButton data-testid="giftpot-save" onClick={submit} disabled={saving} className="flex-1">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Bijdragen"}
                </BrutalButton>
                {pot.mine && (
                  <button data-testid="giftpot-remove" onClick={removeMine} className="brutal-sm brutal-press rounded-xl px-3 bg-white">
                    <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                )}
              </div>
            </div>
            )}

            {(pot.opt_outs || []).length > 0 && (
              <p data-testid="giftpot-optouts" className="text-xs font-semibold text-brand-ink/50">
                Doen niet mee: {(pot.opt_outs).map((o) => o.name).join(", ")}
              </p>
            )}

            {!pot.closed && (
              <button data-testid="giftpot-optout-btn" onClick={async () => {
                try { const { data } = await api.post(`/giftpots/${gid}/opt-out`); toast.success(data.opted_out ? "Je doet niet mee — je krijgt geen herinnering" : "Je doet weer mee 🎉"); await load(); }
                catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); }
              }} className={`w-full brutal-sm brutal-press rounded-xl px-3 py-2 text-sm font-bold ${pot.i_opted_out ? "bg-brand-violet text-white" : "bg-white"}`}>
                {pot.i_opted_out ? "Toch meedoen" : "Ik doe niet mee aan deze pot"}
              </button>
            )}

            {pot.can_close && (
              <button data-testid="giftpot-close-btn" onClick={toggleClosed}
                className={`w-full brutal-sm brutal-press rounded-xl px-3 py-2.5 text-sm font-bold flex items-center justify-center gap-2 ${pot.closed ? "bg-white" : "bg-brand-green text-white"}`}>
                <Check className="w-4 h-4" strokeWidth={2.5} />{pot.closed ? "Pot heropenen" : "Cadeau geregeld — pot afronden"}
              </button>
            )}

            {isCustom && cu?.can_delete && (
              <button data-testid="giftpot-delete-btn" onClick={removePot}
                className="w-full brutal-sm brutal-press rounded-xl px-3 py-2 text-sm font-bold bg-white text-red-600 flex items-center justify-center gap-2">
                <Trash2 className="w-4 h-4" strokeWidth={2.5} />Cadeaupot verwijderen
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GiftPotDialog;
